import { useEffect, useState, useRef } from "react";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";
import { useAutoplayContext } from "../../contexts/AutoplayContext";
import { pushData, safelyRemoveScript, shouldPushData } from "./utils";
import { ChartbeatStrategy } from "./Chartbeat";
import { isEEA } from "./isEEA";

const CHARTBEAT_VIDEO_SCRIPT =
  "https://static.chartbeat.com/js/chartbeat_video.js";

/**
 * Hook to integrate Chartbeat analytics
 * https://docs.chartbeat.com/cbp/feature-integrations/video-engagement/custom-player-integration-sdk
 * @param video video data for the video in the player. a state variable created by a useState call in another component.
 * @param videoElementRef a ref attached to the HTML video element we're sending Chartbeat analytics for
 */
export const useChartbeat = () => {
  const videoElementRef = useVideoElementRefContext();
  const { videoData } = useVideoDataContext();
  const { autoplayState } = useAutoplayContext();

  const [isPlayerPushed, setIsPlayerPushed] = useState(false);

  const signalRef = useRef(null);
  const scriptRef = useRef(null);

  // separate useEffect without dependencies to create a cleanup function that only runs on OrcaPlayer unmount
  useEffect(() => {
    if (process.env.NODE_ENV === "development") return;
    // Create AbortSignal to remove event listeners added in the ChartbeatStrategy class object when OrcaPlayer unmounts
    const controller = new AbortController();
    const { signal } = controller;
    signalRef.current = signal;
    // Create chartbeat_video.js script element
    const script = document.createElement("script");
    script.src = CHARTBEAT_VIDEO_SCRIPT;
    script.async = true;
    script.id = "chartbeat_video";
    scriptRef.current = script;
    return () => {
      safelyRemoveScript("chartbeat_video", controller);
    };
  }, []);

  // useEffect that re-runs when full video data loads
  useEffect(() => {
    if (process.env.NODE_ENV === "development") return;
    const eeaUser = isEEA();
    const shouldPush = shouldPushData(
      isPlayerPushed,
      videoData,
      signalRef.current,
      scriptRef.current,
      eeaUser
    );
    // Push strategy, push player, and load chartbeat script whenever a new video player loads on the page
    if (shouldPush) {
      const player = {
        videoData,
        videoElementRef,
        autoplayType: autoplayState.type,
        signal: signalRef.current
      };
      // Note from Chartbeat documentation: Make sure to push your video strategy constructor to _cbv_strategies synchronously, before Chartbeat's pinger (chartbeat_video.js) loads.
      pushData(player, ChartbeatStrategy, scriptRef.current);
      setIsPlayerPushed(true);
    }
  }, [videoData?._id, signalRef.current, scriptRef.current, autoplayState]);

  return {
    isPlayerPushed
  };
};
